import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faRefresh } from '@fortawesome/free-solid-svg-icons';
import ResponsivePagination from 'react-responsive-pagination';
import Spinner from '../../../shared/spinner';
import createAxiosInstance from '../../../services/axiosConfig';
import { format, parseISO } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { ProtectedAction } from '../../../AccessControl';


const { REACT_APP_ESTEIRA_BACK } = process.env;

const axiosInstance = createAxiosInstance(REACT_APP_ESTEIRA_BACK + '/rest/feriados');

interface Feriado {
  id: number;
  data: string;
  descricao: string;
  tipo: String;
}

const FeriadosComponent = () => {
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [anosDisponiveis, setAnosDisponiveis] = useState<number[]>([]);
  const [anoSelecionado, setAnoSelecionado] = useState<number>(new Date().getFullYear());
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroData, setFiltroData] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('Todos');
  const [loading, setLoading] = useState(false);
  const [filtrosVisiveis, setFiltrosVisiveis] = useState(true);
  const [paginaAtual, setPaginaAtual] = useState(1);

  const navigate = useNavigate();

  const listarFeriados = async (ano: number) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/getFeriados?ano=${ano}`);
      setFeriados(res.data);
    } catch (err) {
      console.error('Erro ao buscar feriados', err);
    }
    setLoading(false);
  };

  const carregarAnos = async () => {
    try {
      const res = await axiosInstance.get('/getAnos');
      setAnosDisponiveis(res.data);
    } catch (err) {
      console.error('Erro ao buscar anos disponíveis', err);
    }
  };

  const excluirFeriado = async (feriado: Feriado) => {
    if (!window.confirm(`Tem certeza que deseja excluir o feriado "${feriado.descricao}"?`)) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`/delFeriados/${feriado.id}`);
      setFeriados(prev => prev.filter(f => f.id !== feriado.id));
      setMensagemSucesso('Feriado excluído com sucesso!');
      const timeout = setTimeout(() => setMensagemSucesso(null), 2000);
    } catch (err) {
      console.error('Erro ao excluir feriado', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    carregarAnos();
  }, []);

  useEffect(() => {
    listarFeriados(anoSelecionado);
  }, [anoSelecionado]);

  const feriadosFiltrados = feriados.filter(f =>
    (!filtroNome || f.descricao.toLowerCase().includes(filtroNome.toLowerCase())) &&
    (!filtroData || f.data.includes(filtroData)) &&
    (filtroTipo === 'Todos' || f.tipo === filtroTipo)
  );

  const location = useLocation();
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);

  useEffect(() => {
    const sucesso = location.state?.sucesso;

    if (sucesso) {
      setMensagemSucesso(sucesso);
      window.history.replaceState({}, document.title); // Limpa o state da URL

      const timeout = setTimeout(() => {
        setMensagemSucesso(null);
      }, 2000); // remove o alerta após 2 segundos

      return () => clearTimeout(timeout);
    }
  }, [location.state]);


  return (
    <div className="position-relative m-md-3">
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
        <h5>Parâmetros Cartão &gt; Feriados</h5>
        <div style={{ display: 'flex', gap: '10px' }}>
          <ProtectedAction>
            <Button onClick={() => navigate('/parametros-feriados/inclusao')} id="App-button-cor-principal-escura">
              + Novo Feriado
            </Button>
          </ProtectedAction>

          <Button id="App-button-cor-principal-escura" onClick={() => setFiltrosVisiveis(!filtrosVisiveis)}>
            <FontAwesomeIcon icon={faFilter} /> Filtros
          </Button>
        </div>
      </div>

      {mensagemSucesso && (
        <div className="alert alert-success" role="alert">
          {mensagemSucesso}
        </div>
      )}

      {filtrosVisiveis && (
        <Row className="mb-4">
          <Form.Group as={Col} md="2">
            <Form.Label>Ano</Form.Label>
            <Form.Select value={anoSelecionado} onChange={(e) => setAnoSelecionado(Number(e.target.value))}>
              {anosDisponiveis.map((ano) => (
                <option key={ano} value={ano}>{ano}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md="2">
            <Form.Label>Data</Form.Label>
            <Form.Control type="text" value={filtroData} onChange={(e) => setFiltroData(e.target.value)} />
          </Form.Group>
          <Form.Group as={Col} md="4">
            <Form.Label>Nome</Form.Label>
            <Form.Control type="text" value={filtroNome} onChange={(e) => setFiltroNome(e.target.value)} />
          </Form.Group>
          <Form.Group as={Col} md="3">
            <Form.Label>Tipo</Form.Label>
            <Form.Select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="Todos">Todos</option>
              <option value="Nacional">Nacional</option>
              <option value="Regional">Regional</option>
            </Form.Select>
          </Form.Group>
        </Row>
      )}

      {loading ? <Spinner loading={loading} /> : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th id="App-header-table">Data</th>
                <th id="App-header-table">Descrição</th>
                <th id="App-header-table">Tipo</th>
                <ProtectedAction>
                  <th id="App-header-table">Opções</th>
                </ProtectedAction>
              </tr>
            </thead>
            <tbody>
              {feriadosFiltrados.sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
              .map((f, idx) => (
                <tr key={idx}>
                  <td>{format(parseISO(f.data), 'dd/MM/yyyy')}</td>
                  <td>{f.descricao}</td>
                  <td>{f.tipo}</td>

                  <ProtectedAction>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => navigate(`/parametros-feriados/edicao/${f.id}`)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => excluirFeriado(f)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </ProtectedAction>
                </tr>
              ))}
            </tbody>
          </table>
          <ResponsivePagination
            current={paginaAtual}
            total={1}
            onPageChange={setPaginaAtual}
          />
        </div>
      )}
    </div>
  );
};

export default FeriadosComponent;
